import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { resolveRichText } from '@vibesites/runtime';
import {
  usePropertyField,
  useContentToken,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from './shared/RichTextContent';
import { PetsAllowedIcon } from '@vibesites/assets';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { usePropertyWithBedroomOptions } from '../hooks/usePropertyWithBedroomOptions';

// Matches an empty <p> tag — empty meaning truly empty, whitespace-only,
// or containing only &nbsp; / <br> filler that some richtext editors emit.
const EMPTY_P = String.raw`<p\b[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>`;
const LEADING_EMPTY_P_RE = new RegExp(`^(?:\\s*${EMPTY_P}\\s*)+`, 'i');
const TRAILING_EMPTY_P_RE = new RegExp(`(?:\\s*${EMPTY_P}\\s*)+$`, 'i');

function stripEdgeEmptyParagraphs(html: string): string {
  return html.replace(LEADING_EMPTY_P_RE, '').replace(TRAILING_EMPTY_P_RE, '').trim();
}

const ctaLinkDef: SlotGroupDefinition = {
  label: 'View Amenities CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/amenities',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Amenities',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

function hasValue(val: unknown): val is string {
  if (val === null || val === undefined) return false;
  const s = String(val).trim().toLowerCase();
  return (
    s !== '' &&
    s !== 'false' &&
    s !== 'null' &&
    s !== 'undefined' &&
    s !== '0' &&
    s !== 'none' &&
    s !== 'n/a' &&
    s !== 'na'
  );
}

function isTruthy(val: unknown): boolean {
  if (val === true) return true;
  if (!val) return false;
  const s = String(val).trim().toLowerCase();
  return s === 'true' || s === 'yes' || s === '1';
}

function formatCurrency(val: string): string {
  const num = parseFloat(val.replace(/[^0-9.]/g, ''));
  if (isNaN(num)) return val;
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

// ============================================================================
// Component
// ============================================================================

export function PetPolicy() {
  const property = usePropertyWithBedroomOptions();
  const allowsCats = usePropertyField('property_allows_cats');
  const allowsDogs = usePropertyField('property_allows_dogs');
  const restrictions = usePropertyField('property_pet_restrictions') as string | null;
  const policyDescription = usePropertyField('property_pet_policy_description') as string | null;
  const policyInfo = usePropertyField('property_petpolicy_info') as string | null;

  // Resolve restrictions richtext ourselves so we can strip leading/trailing
  // empty paragraphs (including <p>&nbsp;</p>, <p><br></p>, whitespace-only)
  // that the CSS `p:empty` rule can't catch.
  const restrictionsRaw = useContentToken('petpolicy.restrictions', {
    defaultField: 'property_pet_restrictions',
  });
  const restrictionsHtml = restrictionsRaw
    ? stripEdgeEmptyParagraphs(resolveRichText(restrictionsRaw, property as Record<string, any>))
    : '';

  const ctaLink = useSlotGroup('petpolicy.cta_link', ctaLinkDef);

  const petDeposit1Sublabel = useContentToken('petpolicy.pet_deposit_1_sublabel');
  const petDeposit2Sublabel = useContentToken('petpolicy.pet_deposit_2_sublabel');

  // Stat values — defaultField in the schema pulls the property value; authors can override per-site.
  const maxPetsValue = useContentToken('petpolicy.max_pets_value');
  const petDeposit1Value = useContentToken('petpolicy.pet_deposit_1_value');
  const petDeposit2Value = useContentToken('petpolicy.pet_deposit_2_value');
  const monthlyRentValue = useContentToken('petpolicy.monthly_rent_value');

  const catsDogsList = [
    isTruthy(allowsDogs) && 'Dogs',
    isTruthy(allowsCats) && 'Cats',
  ].filter(Boolean);

  // Editable welcome text — authors can override it per-site.
  const welcomeText = useContentToken('petpolicy.welcome_text');

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  // Show the welcome badge if either pet type is allowed by the property
  // OR if an author has explicitly set a custom welcome text.
  const showWelcomeBadge = catsDogsList.length > 0 || Boolean(welcomeText);

  const showMaxPets = hasValue(maxPetsValue);
  const showPetDeposit1 = hasValue(petDeposit1Value);
  const showPetDeposit2 = hasValue(petDeposit2Value);
  const showMonthlyRent = hasValue(monthlyRentValue);
  const showStats =
    showMaxPets || showPetDeposit1 || showPetDeposit2 || showMonthlyRent;

  const descriptionHtml = policyDescription || policyInfo || null;

  return (
    <section ref={sectionRef} className="reveal-fade-up py-12 md:py-20 bg-[var(--vibe-color-background)]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="pet-layout">
          {/* Left column: description + restrictions */}
          <div className="pet-content">
            {/* Pets welcome badge */}
            {showWelcomeBadge && (
              <div className="flex items-center gap-3 mb-6">
                <PetsAllowedIcon size={28} color="var(--vibe-color-secondary)" />
                <RichTextContent
                  path="petpolicy.welcome_text"
                  as="span"
                  className="font-heading text-lg text-black font-medium tracking-wide uppercase"
                />
              </div>
            )}

            {/* Policy description */}
            {descriptionHtml && (
              <RichTextContent
                path="petpolicy.description"
                label="Pet Policy Description"
                defaultField="property_pet_policy_description"
                as="div"
                className="font-body text-base md:text-lg leading-relaxed text-[var(--vibe-color-text)] mb-6 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_li]:mb-1 [&_a]:text-primary [&_a]:underline"
              />
            )}

            {/* Pet restrictions */}
            {hasValue(restrictions) && (
              <div className="pet-restrictions">
                <RichTextContent
                  path="petpolicy.restrictions_heading"
                  as="h3"
                  className="font-heading text-base font-medium text-[var(--vibe-color-text)] mb-3 tracking-wide uppercase"
                />
                <EditableToken
                  path="petpolicy.restrictions"
                  type="richtext"
                  label="Pet Restrictions"
                  defaultField="property_pet_restrictions"
                  as="div"
                  className="font-body text-sm leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-2 [&_p:empty]:hidden [&_p:empty]:m-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_li]:mb-0.5 [&_a]:text-primary [&_a]:underline"
                >
                  <span
                    dangerouslySetInnerHTML={{ __html: restrictionsHtml }}
                    style={{ display: 'contents' }}
                  />
                </EditableToken>
              </div>
            )}

            {/* CTA */}
            <div className="mt-8">
              <EditableSlotGroup groupPath="petpolicy.cta_link" definition={ctaLinkDef}>
                <a
                  href={ctaLink.url || '/amenities'}
                  target={ctaLink._target || '_self'}
                  className="pet-cta group/cta relative inline-flex items-center gap-2 px-8 py-3 font-heading text-sm uppercase tracking-wider overflow-hidden transition-all duration-300"
                >
                  <span className="relative z-10">{ctaLink.label || 'View Amenities'}</span>
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* Right column: stat cards — mockup order: max pets, 1-pet deposit, monthly rent, 2-pet deposit */}
          {showStats && (
            <div className="pet-stats">
              {showMaxPets && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.max_pets_label" as="span" className="pet-stat-label" />
                  <EditableToken path="petpolicy.max_pets_value" as="span" className="pet-stat-value">
                    {maxPetsValue ? `${maxPetsValue} Allowed` : ''}
                  </EditableToken>
                </div>
              )}
              {showPetDeposit1 && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.pet_deposit_1_label" as="span" className="pet-stat-label" />
                  <EditableToken path="petpolicy.pet_deposit_1_value" as="span" className="pet-stat-value">
                    {petDeposit1Value ? `${formatCurrency(petDeposit1Value)} for 1 Pet` : ''}
                  </EditableToken>
                  <EditableToken path="petpolicy.pet_deposit_1_sublabel" as="span" className="pet-stat-sublabel">
                    {petDeposit1Sublabel || '(Non-Refundable)'}
                  </EditableToken>
                </div>
              )}
              {showMonthlyRent && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.monthly_rent_label" as="span" className="pet-stat-label" />
                  <EditableToken path="petpolicy.monthly_rent_value" as="span" className="pet-stat-value">
                    {monthlyRentValue ? `${formatCurrency(monthlyRentValue)}/mo/pet` : ''}
                  </EditableToken>
                </div>
              )}
              {showPetDeposit2 && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.pet_deposit_2_label" as="span" className="pet-stat-label" />
                  <EditableToken path="petpolicy.pet_deposit_2_value" as="span" className="pet-stat-value">
                    {petDeposit2Value ? `${formatCurrency(petDeposit2Value)} for 2 Pets` : ''}
                  </EditableToken>
                  <EditableToken path="petpolicy.pet_deposit_2_sublabel" as="span" className="pet-stat-sublabel">
                    {petDeposit2Sublabel || '(Non-Refundable)'}
                  </EditableToken>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .pet-layout {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
        }
        @media (min-width: 768px) {
          .pet-layout {
            grid-template-columns: 1fr 1fr;
            gap: 3rem;
            align-items: start;
          }
        }

        .pet-content {
          order: 2;
        }
        @media (min-width: 768px) {
          .pet-content {
            order: 1;
          }
        }

        .pet-stats {
          order: 1;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.75rem;
        }
        @media (min-width: 768px) {
          .pet-stats {
            order: 2;
          }
        }

        .pet-stat-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 1.25rem 1rem;
          border: 1px solid #e8e8ea;
          background: #f7f7f8;
          border-radius: 4px;
          min-height: 100px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06);
        }

        .pet-stat-label {
          font-family: var(--vibe-font-heading);
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--vibe-color-text);
          margin-bottom: 0.5rem;
        }

        .pet-stat-value {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
        }

        .pet-stat-sublabel {
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          color: var(--vibe-color-text-muted);
          opacity: 0.7;
          margin-top: 0.15rem;
        }

        .pet-restrictions {
          padding: 1rem 1.25rem;
          background: #f7f7f8;
          border-left: 3px solid var(--vibe-color-secondary);
          margin-bottom: 0.5rem;
        }

        /* Outline CTA — same type/border weight as .hero-solid-cta-btn;
           colors stay black text + primary border (page sits on light bg). */
        .pet-cta {
          color: #000;
          background: transparent;
          border: 1.5px solid var(--vibe-color-primary);
          border-radius: 2px;
          text-decoration: none;
          box-shadow: none;
        }
        .pet-cta::before {
          content: '';
          position: absolute;
          inset: 0;
          background: var(--vibe-color-secondary);
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .pet-cta:hover {
          border-color: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary);
          transform: translateY(-1px);
          box-shadow:
            0 2px 6px rgba(0,0,0,0.15),
            0 4px 16px color-mix(in srgb, var(--vibe-color-secondary) 25%, transparent);
        }
        .pet-cta:hover::before {
          opacity: 1;
        }
        .pet-cta:active {
          transform: translateY(0);
          box-shadow: none;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PetPolicy',
  displayName: 'Pet Policy',
  tokens: {
    'petpolicy.welcome_text': {
      label: 'Pets Welcome Text',
      type: 'richtext',
      description: 'Badge text shown next to the paws icon.',
      defaultValue: '<p>Dogs &amp; Cats Welcome</p>',
    },
    'petpolicy.description': {
      label: 'Pet Policy Description',
      type: 'richtext',
      defaultField: 'property_pet_policy_description',
      suggestedFields: ['property_pet_policy_description', 'property_petpolicy_info'],
    },
    'petpolicy.restrictions_heading': {
      label: 'Pet Restrictions Heading',
      type: 'richtext',
      description: 'Heading shown above the pet restrictions list.',
      defaultValue: '<p>Pet Restrictions</p>',
    },
    'petpolicy.restrictions': {
      label: 'Pet Restrictions',
      type: 'richtext',
      defaultField: 'property_pet_restrictions',
    },
    'petpolicy.max_pets_label': {
      label: 'Pets Per Household Label',
      type: 'richtext',
      defaultValue: '<p>Pets Per Household</p>',
    },
    'petpolicy.max_pets_value': {
      label: 'Pets Per Household Value',
      type: 'text',
      defaultField: 'property_max_pets',
      description:
        'Number of pets allowed per household. Pulled from the property automatically; override here to display a custom value.',
    },
    'petpolicy.pet_deposit_1_label': {
      label: 'Pet Deposit (1 Pet) Label',
      type: 'richtext',
      defaultValue: '<p>Pet Deposit</p>',
    },
    'petpolicy.pet_deposit_1_value': {
      label: 'Pet Deposit (1 Pet) Value',
      type: 'text',
      defaultValue: '300',
      description:
        'Deposit amount for 1 pet. Displayed as "$X for 1 Pet". Override per site if amounts differ.',
    },
    'petpolicy.pet_deposit_1_sublabel': {
      label: 'Pet Deposit (1 Pet) Sublabel',
      type: 'text',
      defaultValue: '(Non-Refundable)',
    },
    'petpolicy.monthly_rent_label': {
      label: 'Monthly Pet Rent Label',
      type: 'richtext',
      defaultValue: '<p>Monthly Pet Rent</p>',
    },
    'petpolicy.monthly_rent_value': {
      label: 'Monthly Pet Rent Value',
      type: 'text',
      defaultField: 'property_monthly_pet_rent',
      defaultValue: '25',
      description:
        'Monthly pet rent amount. Displayed as "$X/mo/pet". Pulled from the property automatically; override here to display a custom value.',
    },
    'petpolicy.pet_deposit_2_label': {
      label: 'Pet Deposit (2 Pets) Label',
      type: 'richtext',
      defaultValue: '<p>Pet Deposit</p>',
    },
    'petpolicy.pet_deposit_2_value': {
      label: 'Pet Deposit (2 Pets) Value',
      type: 'text',
      defaultValue: '500',
      description:
        'Deposit amount for 2 pets. Displayed as "$X for 2 Pets". Override per site if amounts differ.',
    },
    'petpolicy.pet_deposit_2_sublabel': {
      label: 'Pet Deposit (2 Pets) Sublabel',
      type: 'text',
      defaultValue: '(Non-Refundable)',
    },
  },
  slotGroups: {
    'petpolicy.cta_link': {
      label: 'View Amenities CTA',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/amenities',
        },
        label: {
          label: 'Link Text',
          type: 'text',
          defaultValue: 'View Amenities',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_self',
        },
      },
    },
  },
};

(PetPolicy as any).contentSchema = contentSchema;

export default PetPolicy;
